import { resizeImgUrl, transformImgUrl } from '../ui/uiutils/image';
import { Mat3x3 } from './mat3x3';
import { isGuidWithoutDashes } from './uuid';

export type FileType = 'image' | 'video';
export type FileClip = {
    aspect: number;
    H: Mat3x3;
};

export const getFileData = async (file: File, url: string, small: boolean, clip?: FileClip): Promise<File | Blob> => {
    if (getFileType(file) !== 'image') return file;

    const size = small ? 516 : 1920;

    if (!clip) {
        const skip = small ? 300 : 900;
        if (file.size / 1000 < skip) return file;
        return await resizeImgUrl(url, size);
    } else {
        return await transformImgUrl(url, clip, size);
    }
};

export const getFileType = (file: File): FileType | undefined => {
    if (file.type.indexOf('video') >= 0) {
        return 'video';
    } else if (file.type.indexOf('image') >= 0) {
        return 'image';
    }
};

export const getFileOrUrlType = (url: string, file: File | undefined): FileType | undefined => {
    if (file) {
        return getFileType(file);
    } else {
        if (hasVideoExtension(url)) {
            return 'video';
        } else return 'image';
    }
};

const hasVideoExtension = (url: string): boolean => {
    const supported = ['.mp4', '.mov', '.webm', '.mkv'];
    for (const idx in supported) if (url.toLowerCase().indexOf(supported[idx]) > 0) return true;
    return false;
};

export const getUrlPath = (url: string | null | undefined): string | null => {
    if (!url) return null;
    try {
        // This works for full URLs like 'http://example.com/path?query=1#hash'
        return new URL(url).pathname;
    } catch (e) {
        // This is a fallback for relative URLs like '/path/to/file?query=1'
        return url.split('?')[0].split('#')[0];
    }
};

export const getUrlPathWithoutLeadingSlash = (url: string | null | undefined): string | null => {
    const path = getUrlPath(url);
    if (path === null) return null;
    return path.startsWith('/') ? path.substring(1) : path;
};

export const getPropFileName = (propId: string, file: File, defaultExt?: string | null): string | null => {
    if (!propId) throw new Error('Prop ID is required!');
    if (!isGuidWithoutDashes(propId)) throw new Error(`Invalid prop ID! ${propId}}`);

    const fileParts = file.name.split('.');
    const ext = fileParts.length > 1 ? fileParts.pop() : defaultExt;

    if (!ext) throw new Error('No extension in file name and no default was provided!');

    return propId + '.' + ext;
};

export const getPropThumbnailName = (file: string | null | undefined) => {
    if (!file) return null;
    const parts = file.split('.');
    if (parts.length < 2) return null;
    parts.pop();
    return parts.join('.') + '.jpg';
};
